import { Component, Input, AfterViewInit, OnDestroy, Inject, OnChanges, SimpleChanges } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-scroll-cue',
  templateUrl: './scroll-cue.component.html',
  styleUrls: ['./scroll-cue.component.scss']
})
export class ScrollCueComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() scrollTarget?: HTMLElement;
  @Input() scrollTargetId?: string;
  @Input() topTarget?: HTMLElement;
  @Input() topTargetId?: string;
  @Input() scrollToTopOnBottom = true;
  @Input() switchTarget?: HTMLElement;
  @Input() switchTargetId?: string;
  @Input() switchOffsetRatio = 0.35;
  @Input() ariaLabel = 'Scroll to the next section';
  @Input() sectionIds?: string[];
  @Input()
  set sections(value: number | string | null | undefined) {
    const numericValue = Number(value);
    const normalized = Number.isFinite(numericValue) ? Math.floor(numericValue) : 2;
    this.sectionsValue = Math.max(1, normalized);
    this.dotItems = Array.from({ length: this.sectionsValue }, (_, index) => index);
    this.activeIndex = Math.min(this.activeIndex, Math.max(0, this.sectionsValue - 1));
  }
  get sections(): number {
    return this.sectionsValue;
  }

  private sectionsValue = 2;

  @Input() scrollContainer: Window | HTMLElement = window;

  isBottom = false;
  activeIndex = 0;
  dotItems: number[] = Array.from({ length: this.sectionsValue }, (_, index) => index);

  private scrollRafId: number | null = null;
  private clickRafId: number | null = null;
  private scrollCleanup?: () => void;
  private isInitialized = false;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  ngAfterViewInit(): void {
    if (typeof window === 'undefined') {
      return;
    }
    this.setupScrollListener();
    this.isInitialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scrollContainer'] && !changes['scrollContainer'].isFirstChange()) {
      if (this.isInitialized) {
        this.removeScrollListener();
        this.setupScrollListener();
      }
    }
  }

  ngOnDestroy(): void {
    this.removeScrollListener();
    if (this.clickRafId !== null) {
      window.cancelAnimationFrame(this.clickRafId);
      this.clickRafId = null;
    }
  }

  private removeScrollListener(): void {
    if (this.scrollCleanup) {
      this.scrollCleanup();
      this.scrollCleanup = undefined;
    }
  }

  onCueClick(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.shouldUseSectionSteps()) {
      const positions = this.getSectionPositions();
      if (positions.length <= 1) {
        return;
      }
      if (this.isBottom) {
        if (this.scrollToTopOnBottom) {
          this.smoothScrollToY(0);
        }
        return;
      }
      const nextIndex = Math.min(this.activeIndex + 1, positions.length - 1);
      this.smoothScrollToY(positions[nextIndex]);
      return;
    }
    const isBottom = this.isBottom;
    const target = isBottom
      ? this.resolveTarget(this.topTarget, this.topTargetId)
      : this.resolveTarget(this.scrollTarget, this.scrollTargetId);

    if (target) {
      const offset = this.getContainerOffset();
      const targetTop = target.getBoundingClientRect().top - offset + this.getScrollTop();
      this.smoothScrollToY(targetTop);
      return;
    }

    if (isBottom && this.scrollToTopOnBottom) {
      this.smoothScrollToY(0);
    }
  }

  private setupScrollListener(): void {
    const update = () => {
      this.scrollRafId = null;
      this.updateState();
    };

    const onScroll = () => {
      if (this.scrollRafId !== null) {
        return;
      }
      this.scrollRafId = window.requestAnimationFrame(update);
    };

    const target = this.scrollContainer;
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    this.scrollCleanup = () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (this.scrollRafId !== null) {
        window.cancelAnimationFrame(this.scrollRafId);
        this.scrollRafId = null;
      }
    };
  }

  private updateState(): void {
    const viewportHeight = this.getViewportHeight();
    const scrollTop = this.getScrollTop();
    const containerOffset = this.getContainerOffset();

    if (this.shouldUseSectionSteps()) {
      const positions = this.getSectionPositions(viewportHeight);
      this.activeIndex = this.getActiveIndex(positions, scrollTop);
      this.isBottom = this.activeIndex >= positions.length - 1;
      return;
    }
    const boundary = this.resolveTarget(
      this.switchTarget ?? this.scrollTarget,
      this.switchTargetId ?? this.scrollTargetId
    );
    let isBottom = false;

    if (boundary) {
      const boundaryTop = boundary.getBoundingClientRect().top - containerOffset + scrollTop;
      const ratio = Math.min(Math.max(this.switchOffsetRatio, 0), 1);
      const switchOffset = viewportHeight * ratio;
      isBottom = scrollTop >= boundaryTop - switchOffset;
    } else {
      const documentHeight = this.getScrollHeight();
      const maxScroll = Math.max(1, documentHeight - viewportHeight);
      isBottom = scrollTop >= maxScroll * 0.5;
    }

    this.isBottom = isBottom;
    const maxIndex = Math.max(0, this.sectionsValue - 1);
    this.activeIndex = isBottom ? Math.min(1, maxIndex) : 0;
  }

  private shouldUseSectionSteps(): boolean {
    const hasAnchorSections = Array.isArray(this.sectionIds) && this.sectionIds.length > 1;
    return this.sectionsValue > 2 || hasAnchorSections;
  }

  private getSectionPositions(viewportHeight?: number): number[] {
    const containerOffset = this.getContainerOffset();
    const scrollTop = this.getScrollTop();

    const targetElements = (this.sectionIds ?? [])
      .map((id) => this.document.getElementById(id))
      .filter((element): element is HTMLElement => Boolean(element));

    if (targetElements.length > 1) {
      return targetElements
        .map((element) => element.getBoundingClientRect().top - containerOffset + scrollTop)
        .filter((value) => Number.isFinite(value))
        .sort((a, b) => a - b);
    }

    const viewHeight = viewportHeight ?? this.getViewportHeight();
    const documentHeight = this.getScrollHeight();
    const maxScroll = Math.max(0, documentHeight - viewHeight);
    if (this.sectionsValue <= 1 || maxScroll === 0) {
      return [0];
    }
    const step = maxScroll / (this.sectionsValue - 1);
    return Array.from({ length: this.sectionsValue }, (_, index) => Math.round(step * index));
  }

  private getActiveIndex(positions: number[], scrollTop: number): number {
    if (positions.length <= 1) {
      return 0;
    }
    const ordered = positions
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => a - b);
    let activeIndex = 0;
    for (let index = 0; index < ordered.length - 1; index += 1) {
      const midpoint = (ordered[index] + ordered[index + 1]) / 2;
      if (scrollTop >= midpoint) {
        activeIndex = index + 1;
      }
    }
    return activeIndex;
  }

  private smoothScrollToY(targetY: number): void {
    if (this.clickRafId !== null) {
      window.cancelAnimationFrame(this.clickRafId);
      this.clickRafId = null;
    }

    const startY = this.getScrollTop();
    const distance = targetY - startY;
    if (Math.abs(distance) < 1) {
      return;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (prefersReducedMotion) {
      this.scrollTo(targetY);
      return;
    }

    const duration = Math.min(900, Math.max(450, Math.abs(distance) * 0.5));
    const startTime = window.performance?.now ? window.performance.now() : Date.now();
    const ease = (t: number) => 0.5 * (1 - Math.cos(Math.PI * t));

    const step = (now: number) => {
      const elapsed = (now - startTime) / duration;
      const progress = Math.min(Math.max(elapsed, 0), 1);
      const eased = ease(progress);
      this.scrollTo(startY + distance * eased);
      if (progress < 1) {
        this.clickRafId = window.requestAnimationFrame(step);
      } else {
        this.clickRafId = null;
      }
    };

    this.clickRafId = window.requestAnimationFrame(step);
  }

  private scrollTo(y: number): void {
    if (this.isWindow(this.scrollContainer)) {
      window.scrollTo(0, y);
    } else {
      this.scrollContainer.scrollTo(0, y);
    }
  }

  private resolveTarget(target?: HTMLElement, targetId?: string): HTMLElement | null {
    if (target) {
      return target;
    }
    if (targetId) {
      return this.document.getElementById(targetId);
    }
    return null;
  }

  private getScrollTop(): number {
    if (this.isWindow(this.scrollContainer)) {
      return window.pageYOffset || this.document.documentElement.scrollTop || 0;
    }
    return this.scrollContainer.scrollTop;
  }

  private getScrollHeight(): number {
    if (this.isWindow(this.scrollContainer)) {
      return this.document.documentElement.scrollHeight || 1;
    }
    return this.scrollContainer.scrollHeight;
  }

  private getViewportHeight(): number {
    if (this.isWindow(this.scrollContainer)) {
      return window.innerHeight || this.document.documentElement.clientHeight || 0;
    }
    return this.scrollContainer.clientHeight;
  }

  private getContainerOffset(): number {
    if (this.isWindow(this.scrollContainer)) {
      return 0;
    }
    return this.scrollContainer.getBoundingClientRect().top;
  }

  private isWindow(target: Window | HTMLElement): target is Window {
    return target === window;
  }

}
